/**
 * App Mode Configuration
 *
 * 1 codebase, 2 versions:
 *   - internal:   Full features, no credits, for VDSC team
 *   - commercial: Simplified UI, credit system, for public users
 *
 * Set via environment variable:
 *   VITE_APP_MODE=internal   (default)
 *   VITE_APP_MODE=commercial
 *
 * Usage:
 *   import { isCommercial, isInternal } from '../config/appMode';
 *   {isCommercial && <CreditShop />}
 *   {isInternal && <AdminDashboard />}
 */

export type AppMode = 'internal' | 'commercial';

export const APP_MODE = (import.meta.env.VITE_APP_MODE || 'internal') as AppMode;

export const isInternal = APP_MODE === 'internal';
export const isCommercial = APP_MODE === 'commercial';

/**
 * Feature flags derived from app mode.
 *
 * Cho phep granular control: mot feature co the on/off
 * doc lap voi app mode neu can.
 *
 * Hien tai tat ca derive tu APP_MODE, sau nay co the
 * chuyen sang remote config (Supabase table) khi can.
 */
export const features = {
  // === Shared (gated by user role, not mode) ===
  adminDashboard: true,  // Both modes — Sidebar gates by isAdmin prop from AuthContext

  // === Internal-only features ===
  trainMode: isInternal,
  debugPanels: isInternal,
  semanticMatchLab: isInternal,
  batchSchedulerUI: isInternal,
  rateLimitMonitor: isInternal,
  promptPreview: isInternal,
  devMode: isInternal,

  // === Commercial-only features ===
  creditSystem: isCommercial,
  creditShop: isCommercial,
  payment: isCommercial,
  landingPage: isCommercial,
  privacyBadges: isCommercial,
  referralProgram: isCommercial,
  onboardingTour: isCommercial,

  // === Shared features (both modes) ===
  translate: true,
  glossary: true,
  qualityCheck: true,
  fileExport: true,
  fileImport: true,
  userAuth: true,
  projectManagement: true,
  multiLanguage: true,

  // === Backend parser (migration: client DOMParser → Worker regex) ===
  backendParser: true, // true = parse/export DOCX qua Worker API (V2 rich text UI)

  // === Future flags (off by default, toggle when ready) ===
  translationMemory: false,
  ocrPdf: false,
  offlineMode: false,
  apiAccess: false,
} as const;

export type FeatureFlags = typeof features;
